// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  FullscreenExitRounded,
  FullscreenRounded,
} from '@/constants/hardfin-standard-icons';
import {
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Typography,
  styled,
  useTheme,
} from '@mui/material';

const MapButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  pointerEvents: 'auto',
  '&:hover': { backgroundColor: theme.palette.grey[100] },
}));

const ControlContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: 'calc(1px * infinity)',
  pointerEvents: 'auto',
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

interface Props {
  /**
   * If true, the map is in fullscreen mode.
   */
  isFullscreen: boolean;

  /**
   * If true, locations with zero assets are shown on the map.
   */
  showEmptyLocations: boolean;

  /**
   * A callback fired when the fullscreen button is toggled on.
   */
  onEnterFullscreen: () => void;

  /**
   * A callback fired when the fullscreen button is toggled off.
   */
  onExitFullscreen: () => void;

  /**
   * A callback fired when the show empty locations switch is toggled on.
   */
  onShowEmptyLocations: () => void;

  /**
   * A callback fired when the show empty locations switch is toggled off.
   */
  onHideEmptyLocations: () => void;
}

/**
 * A control bar that places itself at the top of a relative container. This
 * component renders a switch to toggle the visibility of locations with zero
 * assets and a fullscreen toggle button.
 */
export function LocationMapControlBar(props: Props) {
  const theme = useTheme();

  const handleClickFullscreen = () => {
    if (props.isFullscreen) {
      return props.onExitFullscreen();
    }

    props.onEnterFullscreen();
  };

  const handleChangeShowEmptyLocations = (_: unknown, isChecked: boolean) => {
    if (isChecked) {
      return props.onShowEmptyLocations();
    }

    props.onHideEmptyLocations();
  };

  const locationCountControl = (
    <Switch
      size="small"
      color="primary"
      checked={props.showEmptyLocations}
      onChange={handleChangeShowEmptyLocations}
    />
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        // This container is not visible to the user and is only used to
        // position controls within a relative container. Disable pointer events
        // on it because it is invisible, and then re-enable pointer events on
        // the controls.
        pointerEvents: 'none',
        position: 'absolute',
        top: theme.spacing(1),
        left: theme.spacing(1),
        right: theme.spacing(1),
      }}
    >
      <ControlContainer>
        <FormControlLabel
          labelPlacement="end"
          control={locationCountControl}
          label={<Typography variant="body2">Show zeroes</Typography>}
        />
      </ControlContainer>
      <MapButton size="small" onClick={handleClickFullscreen}>
        {props.isFullscreen ? <FullscreenExitRounded /> : <FullscreenRounded />}
      </MapButton>
    </Stack>
  );
}
